/**
 * Offline Database Sync Service
 *
 * Handles synchronization between the local SQLite database and the backend:
 * - fetchOfflineDb: Download fresh database from server
 * - pushOfflineDb: Upload local changes to server
 *
 * Sync flow:
 * 1. Check for pending local changes (SentFromMobile = 0)
 * 2. If online, push local changes to server
 * 3. Fetch fresh database from server
 * 4. Import fetched database into local SQLite
 */

import Axios from "axios";
import { KuikaAppManager } from "../shared/utilty/kuika-app-manager";
import { transformUrlForElectron } from "./electron-bridge-service";
import KuikaCoreWasmBridgeService from "./kuika-core-wasm-bridge-service";
import OfflineDbPersistenceService from "./offline-db-persistence-service";

interface SyncResult {
  success: boolean;
  error?: string;
  pushedChanges?: boolean;
  fetchedData?: boolean;
}

interface SyncOptions {
  /** Force sync even if no pending changes */
  forceFetch?: boolean;
}

interface PushResult {
  success: boolean;
  error?: string;
  /**
   * The exact base64 payload that was POSTed to the server. The caller must
   * mark synced against THIS payload (Db_MarkPayloadAsSynced), not re-scan
   * the DB, so rows added during the push network window are not marked
   * synced without being sent.
   */
  payload?: string;
}

/**
 * Offline database sync service.
 */
class OfflineSyncService {
  private static instance: OfflineSyncService | null = null;

  private isSyncing = false;

  private constructor() {}

  /**
   * Get the singleton instance.
   */
  static getInstance(): OfflineSyncService {
    if (!OfflineSyncService.instance) {
      OfflineSyncService.instance = new OfflineSyncService();
    }
    return OfflineSyncService.instance;
  }

  /**
   * Check if a sync operation is in progress.
   */
  getIsSyncing(): boolean {
    return this.isSyncing;
  }

  /**
   * Perform a full sync operation:
   * 1. Push local changes to server
   * 2. Fetch fresh data from server
   * 3. Import into local database
   * 4. Persist to storage
   */
  async sync(options: SyncOptions = {}): Promise<SyncResult> {
    if (this.isSyncing) {
      return { success: false, error: "Sync already in progress" };
    }

    this.isSyncing = true;
    const result: SyncResult = { success: false };

    try {
      const wasmService = KuikaCoreWasmBridgeService.getInstance();
      const persistenceService = OfflineDbPersistenceService.getInstance();

      // Ensure WASM is initialized
      await wasmService.initialize();

      // Check if database is initialized
      const isDbInitialized = await wasmService.Db_IsInitialized();
      if (!isDbInitialized) {
        console.log("OfflineSyncService: Database not initialized, initializing...");
        await wasmService.ensureDbInitialized();
      }

      // Step 1: Push local changes if any
      const pendingChangesJson = await wasmService.Db_GetPendingChanges();
      const pendingChanges = JSON.parse(pendingChangesJson);
      const hasPendingChanges = Object.keys(pendingChanges).length > 0;

      if (hasPendingChanges) {
        console.log("OfflineSyncService: Found pending changes, pushing to server");
        const pushResult = await this.pushChanges(options);
        result.pushedChanges = pushResult.success;

        if (pushResult.success && pushResult.payload) {
          // Mark synced EXACTLY the rows that were in the pushed payload.
          // A criteria-based re-scan here would also mark rows added during
          // the push network window — they'd never be sent and be lost.
          await wasmService.Db_MarkPayloadAsSynced(pushResult.payload);
          console.log("OfflineSyncService: Marked pushed payload rows as synced");
        } else {
          console.warn("OfflineSyncService: Failed to push changes", pushResult.error);
        }
      }

      // Step 2: Fetch fresh data from server (if requested or no local data)
      if (options.forceFetch || !hasPendingChanges) {
        console.log("OfflineSyncService: Fetching fresh data from server");
        const fetchResult = await this.fetchData(options);
        result.fetchedData = fetchResult.success;

        if (fetchResult.success && fetchResult.data) {
          // Import fetched data with SentFromMobile reset for editable tables
          await wasmService.Db_ImportDatabaseBase64WithReset(fetchResult.data);
          console.log("OfflineSyncService: Imported fresh data from server with SentFromMobile reset");

          // Update device metadata after import
          await this.updateDeviceMetadata();
        } else if (!fetchResult.success) {
          console.warn("OfflineSyncService: Failed to fetch data", fetchResult.error);
        }
      }

      // Step 3: Persist to storage
      const exportedData = await wasmService.Db_ExportDatabaseBase64();
      await persistenceService.saveDatabase(exportedData);
      console.log("OfflineSyncService: Persisted database to storage");

      result.success = true;
    } catch (error) {
      console.error("OfflineSyncService: Sync failed", error);
      result.error = error instanceof Error ? error.message : "Unknown error";
    } finally {
      this.isSyncing = false;
    }

    return result;
  }

  /**
   * Push local changes to the server.
   */
  async pushChanges(options: SyncOptions = {}): Promise<PushResult> {
    try {
      const wasmService = KuikaCoreWasmBridgeService.getInstance();

      // Ensure the WASM runtime and the offline DB (from IndexedDB) are loaded before
      // reading unsent records. On a fresh page load nothing has triggered initialization
      // yet, so Db_GetUnsentRecordsBase64 would throw "Database not initialized".
      await wasmService.initialize();
      await wasmService.ensureDbInitialized();
      const isDbReady: boolean = await wasmService.Db_IsInitialized();
      if (!isDbReady) {
        return {
          success: false,
          error: "Offline database not available. Fetch the offline database first."
        };
      }

      // Export only unsent records matching criteria (matches iOS/Android behavior)
      const base64Data: string = await wasmService.Db_GetUnsentRecordsBase64();

      // Build API URL using proper backend URL
      const url = `${this.getBackendUrl()}/offlinedb/pushOfflineDb`;

      // Use Axios so interceptors handle auth token, refresh, and offline detection
      await Axios.post(url, { content: base64Data });

      console.log("OfflineSyncService: Successfully pushed changes to server");
      // Return the exact bytes sent so the caller marks synced only what was
      // actually transmitted (Db_MarkPayloadAsSynced), not a fresh DB re-scan.
      return { success: true, payload: base64Data };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      console.error("OfflineSyncService: Push failed", error);
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Fetch fresh data from the server.
   */
  async fetchData(options: SyncOptions = {}): Promise<{ success: boolean; data?: string; error?: string }> {
    try {
      // Build API URL using proper backend URL
      const url = `${this.getBackendUrl()}/offlinedb/fetchOfflineDb`;

      // Use Axios so interceptors handle auth token, refresh, and offline detection
      const response = await Axios.get(url);

      // The API returns { content: base64Data }
      const base64Data = response.data?.content || response.data?.Content;

      if (!base64Data) {
        return { success: false, error: "No data returned from server" };
      }

      console.log("OfflineSyncService: Successfully fetched data from server");
      return { success: true, data: base64Data };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      console.error("OfflineSyncService: Fetch failed", error);
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Initialize local database from persisted storage.
   * Call this on app startup to restore the previous database state.
   */
  async initializeFromStorage(): Promise<boolean> {
    try {
      const wasmService = KuikaCoreWasmBridgeService.getInstance();
      const persistenceService = OfflineDbPersistenceService.getInstance();

      // Initialize services
      await wasmService.initialize();
      await persistenceService.initialize();

      // Check for persisted database
      const persistedData = await persistenceService.loadDatabase();

      if (persistedData) {
        console.log("OfflineSyncService: Found persisted database, importing");
        await wasmService.Db_ImportDatabaseBase64(persistedData);

        // Update device metadata after restore
        await this.updateDeviceMetadata();
        return true;
      }
      console.log("OfflineSyncService: No persisted database found, initializing empty database");
      await wasmService.Db_Initialize();
      return false;
    } catch (error) {
      console.error("OfflineSyncService: Failed to initialize from storage", error);
      return false;
    }
  }

  /**
   * Persist current database state to storage.
   * Call this before app closes or goes to background.
   */
  async persistToStorage(): Promise<boolean> {
    try {
      const wasmService = KuikaCoreWasmBridgeService.getInstance();
      const persistenceService = OfflineDbPersistenceService.getInstance();

      // Check if database is initialized
      const isInitialized = await wasmService.Db_IsInitialized();
      if (!isInitialized) {
        console.log("OfflineSyncService: Database not initialized, nothing to persist");
        return true;
      }

      // Export and save
      const base64Data = await wasmService.Db_ExportDatabaseBase64();
      await persistenceService.saveDatabase(base64Data);

      console.log("OfflineSyncService: Persisted database to storage");
      return true;
    } catch (error) {
      console.error("OfflineSyncService: Failed to persist to storage", error);
      return false;
    }
  }

  /**
   * Check if there are pending changes that haven't been synced.
   */
  async hasPendingChanges(): Promise<boolean> {
    try {
      const wasmService = KuikaCoreWasmBridgeService.getInstance();

      const isInitialized = await wasmService.Db_IsInitialized();
      if (!isInitialized) {
        return false;
      }

      const pendingChangesJson = await wasmService.Db_GetPendingChanges();
      const pendingChanges = JSON.parse(pendingChangesJson);

      return Object.keys(pendingChanges).length > 0;
    } catch (error) {
      console.error("OfflineSyncService: Failed to check pending changes", error);
      return false;
    }
  }

  /**
   * Delete the offline database (local and persisted).
   */
  async deleteOfflineDatabase(): Promise<void> {
    try {
      const wasmService = KuikaCoreWasmBridgeService.getInstance();
      const persistenceService = OfflineDbPersistenceService.getInstance();

      // Close WASM database
      const isInitialized = await wasmService.Db_IsInitialized();
      if (isInitialized) {
        await wasmService.Db_Close();
      }

      // Delete persisted data
      await persistenceService.deleteDatabase();

      console.log("OfflineSyncService: Deleted offline database");
    } catch (error) {
      console.error("OfflineSyncService: Failed to delete offline database", error);
      throw error;
    }
  }

  // #region Private Methods

  private getBackendUrl(): string {
    // Use the configured backend URL (same pattern as other services)
    let url = KuikaAppManager.GetBackendUrl();
    // Transform URL for Electron environment
    url = transformUrlForElectron(url);
    // Remove trailing slash
    return url.replace(/\/$/, "");
  }

  /**
   * Update device metadata in KuikaDbInfo after importing a new database.
   * This ensures the device is properly identified for sync operations.
   */
  private async updateDeviceMetadata(): Promise<void> {
    try {
      const wasmService = KuikaCoreWasmBridgeService.getInstance();

      // Get or generate device ID
      const deviceId = this.getOrCreateDeviceId();

      // Get device name from user agent
      const deviceName = navigator.userAgent;

      // Get bundle ID from hostname/origin
      const bundleId = window.location.hostname || window.location.origin;

      // Update metadata in database
      await wasmService.Db_UpdateDeviceMetadata(deviceId, deviceName, bundleId, null);

      console.log("OfflineSyncService: Updated device metadata");
    } catch (error) {
      console.warn("OfflineSyncService: Failed to update device metadata", error);
    }
  }

  /**
   * Get or create a persistent device ID.
   * Stored in localStorage to persist across sessions.
   */
  private getOrCreateDeviceId(): string {
    const storageKey = "kuika_offline_device_id";

    // Try to get existing device ID
    let deviceId = localStorage.getItem(storageKey);

    if (!deviceId) {
      // Generate a new UUID-like device ID
      deviceId = this.generateUuid();
      localStorage.setItem(storageKey, deviceId);
      console.log("OfflineSyncService: Generated new device ID:", deviceId);
    }

    return deviceId;
  }

  /**
   * Generate a UUID v4 string.
   */
  private generateUuid(): string {
    // Use crypto.randomUUID if available (modern browsers)
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }

    // Fallback for older browsers
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  // #endregion Private Methods
}

export default OfflineSyncService;
